import type { EditorialStatus, LegalCitation, LegalSource } from '@buakei/types';
import {
  Badge,
  Button,
  DIMENSION_LABELS,
  DataOriginBadge,
  EmptyState,
  ErrorState,
  LoadingState,
  PreviewAction,
  VerificationDimensions,
} from '@buakei/ui';
import { useQuery } from '@tanstack/react-query';
import { useId, useState } from 'react';
import { useInspector } from '@/inspector/inspector-context';
import { demoContentService } from '@/services/content';
import { demoLegalSourceService } from '@/services/legal-sources';
import { useReaderTokens } from '@/pages/library/LibraryPages';
import { Page } from '@/shell/Page';
import { LiveReviewQueue } from './LiveEditorial';
import { STATUS_LABEL } from './status';

/** Real accounts review the actual corpus (ADR-023); demo personas keep the demonstration. */
export function EditorialQueuePage() {
  const tokens = useReaderTokens();
  return tokens ? <LiveReviewQueue tokens={tokens} /> : <DemoEditorialQueue />;
}

function DemoEditorialQueue() {
  const tasks = useQuery({
    queryKey: ['editorial-tasks'],
    queryFn: demoContentService.listEditorialTasks,
  });
  const filterId = useId();
  const [status, setStatus] = useState<EditorialStatus | 'all'>('all');
  const visible = (tasks.data ?? []).filter((t) => status === 'all' || t.status === status);

  return (
    <Page
      title="Editorial review queue"
      description="Each verification dimension is reviewed separately by a person. The AI can never mark a source as reviewed."
      width="wide"
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div className="flex flex-col gap-1">
          <label htmlFor={filterId} className="text-sm font-medium text-navy-900">
            Filter by status
          </label>
          <select
            id={filterId}
            value={status}
            onChange={(e) => setStatus(e.target.value as EditorialStatus | 'all')}
            className="h-10 rounded-md border border-line-200 bg-white px-2 text-ink-900"
          >
            <option value="all">All statuses</option>
            {(Object.keys(STATUS_LABEL) as EditorialStatus[]).map((s) => (
              <option key={s} value={s}>
                {STATUS_LABEL[s].label}
              </option>
            ))}
          </select>
        </div>
        <PreviewAction reason="Not in this demonstration. Decide on library documents in the Review queue.">
          Record decision
        </PreviewAction>
      </div>

      {tasks.isPending ? <LoadingState label="Loading review queue" /> : null}
      {tasks.isError ? (
        <ErrorState title="The queue could not be loaded" onRetry={() => void tasks.refetch()} />
      ) : null}
      {tasks.data && visible.length === 0 ? (
        <EmptyState title="No tasks match this filter" />
      ) : null}
      {visible.length > 0 ? (
        <div
          role="region"
          aria-label="Editorial tasks table"
          tabIndex={0}
          className="overflow-x-auto rounded-lg border border-line-200 bg-white"
        >
          <table className="w-full min-w-[40rem] text-left text-sm">
            <caption className="sr-only">Editorial tasks (demo)</caption>
            <thead className="border-b border-line-200 bg-soft-white text-navy-900">
              <tr>
                <th scope="col" className="p-3 font-semibold">
                  Task
                </th>
                <th scope="col" className="p-3 font-semibold">
                  Dimension
                </th>
                <th scope="col" className="p-3 font-semibold">
                  Status
                </th>
                <th scope="col" className="p-3 font-semibold">
                  Assignee
                </th>
                <th scope="col" className="p-3 font-semibold">
                  Updated
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-line-200">
              {visible.map((t) => (
                <tr key={t.id}>
                  <td className="p-3 text-ink-900">
                    <span className="flex flex-wrap items-center gap-2">
                      {t.title} <DataOriginBadge origin={t.dataOrigin} />
                    </span>
                  </td>
                  <td className="p-3 text-ink-900">{DIMENSION_LABELS[t.dimension].name}</td>
                  <td className="p-3">
                    <Badge tone={STATUS_LABEL[t.status].tone}>{STATUS_LABEL[t.status].label}</Badge>
                  </td>
                  <td className="p-3 text-ink-900">{t.assigneeLabel}</td>
                  <td className="p-3 text-muted-600">{t.updatedAt}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}
    </Page>
  );
}

function firstPassageCitation(source: LegalSource): LegalCitation | null {
  const version = source.versions[0];
  const passage = version?.passages[0];
  if (!version || !passage) return null;
  return {
    id: `registry_${source.id}`,
    sourceId: source.id,
    versionId: version.id,
    passageIds: [passage.id],
    label: `${source.title} — ${passage.label}`,
    dataOrigin: source.dataOrigin,
  };
}

export function SourceRegistryPage() {
  const sources = useQuery({ queryKey: ['sources'], queryFn: demoLegalSourceService.listSources });
  const { open } = useInspector();
  return (
    <Page
      title="Source registry"
      description="Approved sources with provenance, rights notes and per-dimension verification."
      width="wide"
      actions={
        <PreviewAction reason="Not yet available: documents enter the library through the collection pipeline.">
          Register source
        </PreviewAction>
      }
    >
      {sources.isPending ? <LoadingState label="Loading sources" /> : null}
      {sources.isError ? (
        <ErrorState title="Sources could not be loaded" onRetry={() => void sources.refetch()} />
      ) : null}
      {sources.data?.length === 0 ? <EmptyState title="No sources registered" /> : null}
      <ul className="grid gap-4 lg:grid-cols-2">
        {sources.data?.map((s) => {
          const version = s.versions[0];
          const citation = firstPassageCitation(s);
          return (
            <li
              key={s.id}
              className="flex flex-col gap-3 rounded-lg border border-line-200 bg-white p-4"
            >
              <div className="flex flex-wrap items-center gap-2">
                <DataOriginBadge origin={s.dataOrigin} />
                <span className="text-xs text-muted-600 capitalize">{s.kind}</span>
              </div>
              <h2 className="font-serif text-lg font-semibold text-navy-900">{s.title}</h2>
              <p className="text-sm text-muted-600">{s.provenance.acquisition}</p>
              {version ? (
                <VerificationDimensions verification={version.verification} compact />
              ) : null}
              {citation ? (
                <Button
                  variant="secondary"
                  size="sm"
                  className="self-start"
                  onClick={(e) => open(citation, e.currentTarget)}
                >
                  Inspect source
                </Button>
              ) : null}
            </li>
          );
        })}
      </ul>
    </Page>
  );
}
